import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { CalendarClockIcon } from 'lucide-react'
import { toast } from 'sonner'

import { bookingSettingsApi } from '@/api/booking'
import { ApiError } from '@/api/errors'
import type { BookingSettingsInput } from '@/api/types'
import { BookingSettingsForm } from '@/components/booking/booking-settings-form'
import { EmptyState } from '@/components/empty-state'
import { Frame } from '@/components/frame'
import { Skeleton } from '@/components/ui/skeleton'

/** 1.71.0 : Paramètres › Disponibilités — le chemin que nomme le contrat. */
export const Route = createFileRoute('/_app/settings/booking')({
  component: SettingsBooking,
})

export const BOOKING_SETTINGS_KEY = ['me', 'booking-settings'] as const

function SettingsBooking() {
  const queryClient = useQueryClient()
  const settings = useQuery({
    queryKey: BOOKING_SETTINGS_KEY,
    queryFn: () => bookingSettingsApi.mine(),
  })
  const save = useMutation({
    mutationFn: (payload: BookingSettingsInput) => bookingSettingsApi.save(payload),
    onSuccess: (saved) => {
      queryClient.setQueryData(BOOKING_SETTINGS_KEY, saved)
      toast.success(
        saved.enabled
          ? 'Disponibilités enregistrées : les clients peuvent réserver.'
          : 'Disponibilités enregistrées. La réservation est fermée.',
      )
    },
  })

  if (settings.isPending) {
    return (
      <Frame>
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-40 w-full" />
      </Frame>
    )
  }
  if (settings.isError) {
    return (
      <EmptyState
        icon={CalendarClockIcon}
        title="Impossible de lire vos disponibilités"
        description={
          settings.error instanceof ApiError ? settings.error.message : 'Réessayez dans un instant.'
        }
      />
    )
  }
  return (
    <BookingSettingsForm
      key={settings.data.updated_at ?? 'neuf'}
      settings={settings.data}
      pending={save.isPending}
      error={save.error}
      onSave={(payload) => {
        save.mutate(payload)
      }}
    />
  )
}
